import _ from 'lodash';
import i18next from 'i18next';

import { ConfirmationModal } from '@commons/Modals/ConfirmationModal';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';

import ManageB2bCatalogsModal from '../components/ManageB2bCatalogsModal';

import { getEnableableProducts } from './enableProducts';

export const getDeleteProductsModalParams = (
  products,
  handleProductsDeletion,
  isInCentralKitchenOnlyPage,
) => ({
  type: 'warning',
  width: '542px',
  height: 'auto',
  icon: '/images/inpulse/delete-ip-white-small.svg',
  title:
    products.length === 1
      ? i18next.t('ADMIN.PRODUCTS.DELETE_PRODUCT')
      : i18next.t('ADMIN.PRODUCTS.DELETE_PRODUCTS'),
  component: ConfirmationModal,
  data: {
    shouldInterpretHtml: true,
    content: i18next.t(
      isInCentralKitchenOnlyPage
        ? 'ADMIN.CENTRAL_PRODUCTS.DELETE_PRODUCT_MODAL_CONTENT'
        : 'ADMIN.PRODUCTS.DELETE_PRODUCT_MODAL_CONTENT',
      {
        count: products.length,
        name: _.get(products[0], 'name', ''),
      },
    ),
  },
  actions: [
    {
      key: 0,
      color: 'inpulse-outline',
      label: i18next.t('GENERAL.CANCEL'),
      icon: '/images/inpulse/close-black-small.svg',
    },
    {
      key: 1,
      preventClosing: true,
      color: 'inpulse-default',
      label: i18next.t('GENERAL.DELETE'),
      icon: '/images/inpulse/delete-ip-white-small.svg',
      handleClick: () => {
        handleProductsDeletion(products);
      },
    },
  ],
});

export const getDisableProductsModalParams = (
  products,
  disableProducts,
  isInCentralKitchenOnlyPage = false,
) => ({
  type: 'warning',
  width: '542px',
  height: 'auto',
  icon: '/images/inpulse/power-white-small.svg',
  title: i18next.t(
    isInCentralKitchenOnlyPage
      ? 'ADMIN.PRODUCTS.DISABLE_KITCHEN_PRODUCTS_MODAL_TITLE'
      : 'ADMIN.PRODUCTS.DISABLE_PRODUCTS_MODAL_TITLE',
    {
      count: products.length,
    },
  ),
  component: ConfirmationModal,
  data: {
    content: i18next.t(
      isInCentralKitchenOnlyPage
        ? 'ADMIN.PRODUCTS.DISABLE_KITCHEN_PRODUCTS_MODAL_CONTENT'
        : 'ADMIN.PRODUCTS.DISABLE_PRODUCTS_MODAL_CONTENT',
      {
        count: products.length,
        name: _.get(products[0], 'name', ''),
      },
    ),
  },
  actions: [
    {
      key: 0,
      color: 'inpulse-outline',
      label: i18next.t('GENERAL.CANCEL'),
      icon: '/images/inpulse/close-black-small.svg',
    },
    {
      key: 1,
      color: 'inpulse-default',
      label: i18next.t('GENERAL.DISABLE'),
      icon: '/images/inpulse/power-white-small.svg',
      handleClick: async () => {
        await disableProducts(products);
      },
    },
  ],
});

/**
 * Warns that the products composed of an inactive component will be skipped, when the selection holds any
 */
const getEnableProductsModalContent = (products, isInCentralKitchenOnlyPage) => {
  if (getEnableableProducts(products).length < products.length) {
    return i18next.t(
      isInCentralKitchenOnlyPage
        ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_PARTIAL_MODAL_CONTENT'
        : 'ADMIN.PRODUCTS.ENABLE_PRODUCTS_PARTIAL_MODAL_CONTENT',
    );
  }

  return i18next.t(
    isInCentralKitchenOnlyPage
      ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_MODAL_CONTENT'
      : 'ADMIN.PRODUCTS.ENABLE_PRODUCTS_MODAL_CONTENT',
    {
      count: products.length,
      name: _.get(products[0], 'name', ''),
    },
  );
};

export const getEnableProductsModalParams = (
  products,
  enableProducts,
  isInCentralKitchenOnlyPage = false,
) => ({
  type: 'warning',
  width: '542px',
  height: 'auto',
  icon: '/images/inpulse/power-white-small.svg',
  title: i18next.t(
    isInCentralKitchenOnlyPage
      ? 'ADMIN.PRODUCTS.ENABLE_KITCHEN_PRODUCTS_MODAL_TITLE'
      : 'ADMIN.PRODUCTS.ENABLE_PRODUCTS_MODAL_TITLE',
    {
      count: products.length,
    },
  ),
  component: ConfirmationModal,
  data: {
    content: getEnableProductsModalContent(products, isInCentralKitchenOnlyPage),
  },
  actions: [
    {
      key: 0,
      color: 'inpulse-outline',
      label: i18next.t('GENERAL.CANCEL'),
      icon: '/images/inpulse/close-black-small.svg',
    },
    {
      key: 1,
      color: 'inpulse-default',
      label: i18next.t('GENERAL.ACTIVATE'),
      icon: '/images/inpulse/power-white-small.svg',
      handleClick: async () => {
        const productIds = getEnableableProducts(products).map(({ id }) => id);
        await enableProducts(productIds);
      },
    },
  ],
});

/**
 * "Gérer les catalogues client" modal (bulk add / remove of the selected products to external
 * client catalogs). The caller owns the selection / mode states and re-issues this config through
 * refreshGenericModal so the Save button's disabled state stays in sync.
 */
export const getManageB2bCatalogsModalParams = ({
  clients,
  selectedClients,
  setSelectedClients,
  selectedMode,
  setSelectedMode,
  handleSave,
  handleCloseCleanUp,
}) => ({
  type: 'action',
  width: '542px',
  height: 'auto',
  // Let the clients dropdown panel overlay the modal footer instead of being clipped
  applyOverflow: false,
  icon: '/images/inpulse/pen-black-small.svg',
  title: i18next.t('ADMIN.PRODUCTS.B2B_CATALOGS_MANAGE_MODAL_TITLE'),
  component: ManageB2bCatalogsModal,
  handleCloseCleanUp,
  data: {
    clients,
    selectedClients,
    setSelectedClients,
    selectedMode,
    setSelectedMode,
  },
  actions: [
    GENERIC_MODAL_CANCEL_BUTTON(),
    {
      ...GENERIC_MODAL_SAVE_BUTTON(),
      isDisabled: !selectedClients.length,
      handleClick: handleSave,
    },
  ],
});
